import { useState } from "react";
import { Card, CardContent,} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { toast } from "@/hooks/use-toast";
import {
  Copy,
  CheckCircle2,
  Database,
  Zap,
  Brain,
  ArrowRight,
  FileCode,
  Shield,
  Workflow,
  BookOpen,
} from "lucide-react";
import { CollapsibleSection } from "../ui/CollapsibleSection";



function generateFullPrompt(data) {
  let prompt = `# ${data.moduleName} — Full Module Specification\n\n`;
  prompt += `## Purpose\n${data.purpose}\n\n`;
  prompt += `## Tech Stack\n${data.techStack}\n\n`;
  prompt += `## Version\n${data.version}\n\n`;

  prompt += `## Business Rules & Validation\n`;
  data.rules.forEach((r, i) => { prompt += `${i + 1}. ${r}\n`; });
  prompt += `\n`;

  prompt += `## Logic & Calculations\n`;
  data.logic.forEach((l, i) => { prompt += `${i + 1}. ${l}\n`; });
  prompt += `\n`;

  prompt += `## Process Flow\n`;
  data.processFlow.forEach((s, i) => { prompt += `${i + 1}. ${s}\n`; });
  prompt += `\n`;

  prompt += `## Related Documents / Modules\n`;
  data.relatedDocuments.forEach((r) => { prompt += `- **${r.module}**: ${r.relationship}\n`; });
  prompt += `\n`;

  prompt += `## Database Tables\n`;
  data.databaseTables.forEach((t) => {
    prompt += `### ${t.name}\n- Purpose: ${t.purpose}\n- Key Columns: ${t.keyColumns.join(", ")}\n\n`;
  });

  prompt += `## Edge Functions\n`;
  if (data.edgeFunctions.length === 0) {
    prompt += `None currently.\n\n`;
  } else {
    data.edgeFunctions.forEach((f) => {
      prompt += `### ${f.name}\n- Purpose: ${f.purpose}\n- Inputs: ${f.inputs}\n- Outputs: ${f.outputs}\n\n`;
    });
  }

  prompt += `## AI Features\n`;
  if (data.aiFeatures.length === 0) {
    prompt += `None currently.\n\n`;
  } else {
    data.aiFeatures.forEach((a) => { prompt += `- **${a.feature}**: ${a.purpose}\n`; });
    prompt += `\n`;
  }

  prompt += `## End-to-End Flow\n`;
  data.endToEndFlow.forEach((s, i) => { prompt += `${i + 1}. ${s}\n`; });

  return prompt;
}


export function ModulePromptTab({ data }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = generateFullPrompt(data);
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast({ title: "Copied to clipboard", description: "Full module prompt copied successfully" });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4">
      {/* Copy Button */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-lg">{data.moduleName}</h3>
              <p className="text-sm text-muted-foreground">{data.purpose}</p>
            </div>
            <Button onClick={handleCopy} variant={copied ? "default" : "outline"} className="gap-2">
              {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied!" : "Copy Full Prompt"}
            </Button>
          </div>
          <div className="flex gap-2 mt-2">
            <Badge variant="outline">{data.version}</Badge>
            <Badge variant="secondary">{data.techStack}</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Rules */}
      <CollapsibleSection title="Business Rules & Validation" icon={Shield} defaultOpen={false}>
        <ol className="list-decimal list-inside space-y-1.5 text-sm text-muted-foreground">
          {data.rules.map((r, i) => <li key={i}>{r}</li>)}
        </ol>
      </CollapsibleSection>

      {/* Logic */}
      <CollapsibleSection title="Logic & Calculations" icon={Zap} defaultOpen={false}>
        <ol className="list-decimal list-inside space-y-1.5 text-sm text-muted-foreground">
          {data.logic.map((l, i) => <li key={i}>{l}</li>)}
        </ol>
      </CollapsibleSection>

      {/* Process Flow */}
      <CollapsibleSection title="Process Flow" icon={Workflow} defaultOpen={false}>
        <ol className="list-decimal list-inside space-y-1.5 text-sm text-muted-foreground">
          {data.processFlow.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      </CollapsibleSection>

      {/* Related Documents */}
      <CollapsibleSection title="Related Documents / Modules" icon={ArrowRight} defaultOpen={false}>
        <div className="space-y-2">
          {data.relatedDocuments.map((r, i) => (
            <div key={i} className="flex items-start gap-2 text-sm">
              <Badge variant="outline" className="shrink-0 mt-0.5">{r.module}</Badge>
              <span className="text-muted-foreground">{r.relationship}</span>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* Database */}
      <CollapsibleSection title="Database Schema" icon={Database} defaultOpen={false}>
        <div className="space-y-3">
          {data.databaseTables.map((t, i) => (
            <div key={i} className="p-3 rounded-lg bg-muted/50 border">
              <div className="flex items-center gap-2 mb-1">
                <code className="text-xs font-mono font-semibold">{t.name}</code>
              </div>
              <p className="text-xs text-muted-foreground mb-1">{t.purpose}</p>
              <div className="flex flex-wrap gap-1">
                {t.keyColumns.map((c) => (
                  <Badge key={c} variant="secondary" className="text-[10px] font-mono">{c}</Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* Edge Functions */}
      <CollapsibleSection title="Edge Functions" icon={FileCode} defaultOpen={false}>
        {data.edgeFunctions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No edge functions currently.</p>
        ) : (
          <div className="space-y-3">
            {data.edgeFunctions.map((f, i) => (
              <div key={i} className="p-3 rounded-lg bg-muted/50 border">
                <code className="text-xs font-mono font-semibold">{f.name}</code>
                <p className="text-xs text-muted-foreground mt-1">{f.purpose}</p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div><span className="font-medium">Inputs:</span> <span className="text-muted-foreground">{f.inputs}</span></div>
                  <div><span className="font-medium">Outputs:</span> <span className="text-muted-foreground">{f.outputs}</span></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* AI Features */}
      <CollapsibleSection title="AI Features" icon={Brain} defaultOpen={false}>
        {data.aiFeatures.length === 0 ? (
          <p className="text-sm text-muted-foreground">No AI features currently.</p>
        ) : (
          <div className="space-y-2">
            {data.aiFeatures.map((a, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <Brain className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium">{a.feature}</span>
                  <span className="text-muted-foreground"> — {a.purpose}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* End-to-End Flow */}
      <CollapsibleSection title="End-to-End Module Flow" icon={BookOpen} defaultOpen={false}>
        <ol className="list-decimal list-inside space-y-1.5 text-sm text-muted-foreground">
          {data.endToEndFlow.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      </CollapsibleSection>
    </div>
  );
}
